import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import type { Profile } from '@/lib/database.types';

type Props = {
  profile: Profile | null;
  value: boolean;
  onChange: (v: boolean) => void;
};

/**
 * Επιλογή επισύναψης των όρων συνεργασίας του επαγγελματία (η 2σέλιδη
 * "επαγγελματική" έκδοση του PDF, με το λογότυπο της επιχείρησής του) κατά
 * τη δημιουργία προσφοράς/συμφωνίας. Εμφανίζεται μόνο σε επαγγελματίες
 * (portfolio_enabled). Αν δεν έχει ακόμα γράψει όρους, δείχνει συντόμευση
 * προς το αντίστοιχο πεδίο στο προφίλ αντί για το checkbox.
 */
export function ProfessionalTermsOption({ profile, value, onChange }: Props) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const router = useRouter();

  if (!profile?.portfolio_enabled) return null;

  const hasTerms = !!(profile as Profile & { professional_terms?: string | null }).professional_terms?.trim();

  if (!hasTerms) {
    return (
      <Pressable
        style={styles.shortcutBox}
        onPress={() => router.push('/(app)/professional-account' as any)}
        accessibilityRole="button"
      >
        <Ionicons name="document-text-outline" size={18} color={colors.primary} />
        <Text style={styles.shortcutText}>{t('professionalTermsOption.noTermsYet')}</Text>
        <View style={styles.shortcutLink}>
          <Text style={styles.shortcutLinkText}>{t('professionalTermsOption.action_addTerms')}</Text>
          <Ionicons name="chevron-forward" size={14} color={colors.primary} />
        </View>
      </Pressable>
    );
  }

  return (
    <Pressable style={styles.row} onPress={() => onChange(!value)} accessibilityRole="checkbox" accessibilityState={{ checked: value }}>
      <View style={[styles.checkbox, value && styles.checkboxOn]}>
        {value && <Ionicons name="checkmark" size={14} color="#fff" />}
      </View>
      <View style={{ flex: 1 }}>
        <Text style={styles.label}>{t('professionalTermsOption.attachLabel')}</Text>
        <Text style={styles.sublabel}>{t('professionalTermsOption.attachSublabel')}</Text>
      </View>
    </Pressable>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    row: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: spacing.sm,
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.md,
    },
    checkbox: {
      width: 20, height: 20, borderRadius: 5,
      borderWidth: 1.5, borderColor: colors.border,
      alignItems: 'center', justifyContent: 'center',
      marginTop: 1,
    },
    checkboxOn: { backgroundColor: colors.primary, borderColor: colors.primary },
    label: { color: colors.text, fontSize: fontSize.sm, fontWeight: '600' },
    sublabel: { color: colors.textMuted, fontSize: fontSize.xs, marginTop: 2, lineHeight: 16 },
    shortcutBox: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: spacing.xs,
      backgroundColor: 'rgba(201,168,67,0.08)',
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: 'rgba(201,168,67,0.20)',
      padding: spacing.md,
    },
    shortcutText: { color: colors.textMuted, fontSize: fontSize.xs, flexShrink: 1 },
    shortcutLink: { flexDirection: 'row', alignItems: 'center', gap: 2 },
    shortcutLinkText: { color: colors.primary, fontSize: fontSize.xs, fontWeight: '700' },
  });
}
